// ===================== GridsterContainerComponent =====================

import {
  Component, Input, Output, EventEmitter,
  OnChanges, AfterViewInit, ViewChild, ElementRef,
  TemplateRef
} from '@angular/core';
import { GridsterComponent, GridsterConfig, GridsterItem } from 'angular-gridster2';
import { Subject, debounceTime } from 'rxjs';

export interface GridsterWidget {
  template: TemplateRef<any>;
  item?: GridsterItem;
}

@Component({
  selector: 'tart-gridster-container',
  templateUrl: './gridster-container.component.html',
  styleUrls: ['./gridster-container.component.scss']
})
export class GridsterContainerComponent implements OnChanges, AfterViewInit {

  @ViewChild(GridsterComponent) gridster: GridsterComponent;
  @ViewChild('root', { static: true }) root: ElementRef;

  @Input() widgets: GridsterWidget[] = [];
  @Input() editable = false;

  @Output() layoutChange = new EventEmitter<GridsterItem[]>();
  @Output() itemClick = new EventEmitter<number>();

  options: GridsterConfig;
  resizeObserver: ResizeObserver;

  private layout$ = new Subject<GridsterItem[]>();
  private resizeInProgress = false;

  ngAfterViewInit() {
    this.observeSize();

    this.layout$
      .pipe(debounceTime(50))
      .subscribe(l => this.layoutChange.emit(l));
  }

  ngOnChanges() {
    this.applyLayout();
  
    if (!this.options) {
      this.buildOptions();
    }
  }
  
  observeSize() {
    this.resizeObserver = new ResizeObserver(() => {

      if (
        this.gridster?.dragInProgress ||
        this.gridster?.movingItem ||
        this.resizeInProgress
      ) return;
      
      
      const rect = this.root.nativeElement.getBoundingClientRect();

      const cellW = Math.max(rect.width,1) / 3;
      const cellH = Math.max(rect.height,1) / 3;

      if (!this.options) return;

      if (
        this.options.fixedColWidth === cellW &&
        this.options.fixedRowHeight === cellH
      ) return;

      this.options.fixedColWidth = cellW;
      this.options.fixedRowHeight = cellH;

      requestAnimationFrame(() => this.refreshGrid());
    });

    this.resizeObserver.observe(this.root.nativeElement);
  }

  applyLayout() {
    const positions = [
      { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 2, y: 1 },
      { x: 1, y: 2 }, { x: 0, y: 0 }, { x: 2, y: 0 },
      { x: 0, y: 1 }, { x: 0, y: 2 }, { x: 2, y: 2 }
    ];
  
    this.widgets.forEach((w, i) => {
      if (!w.item) {
        w.item = {
          cols: 1,
          rows: 1,
          x: positions[i]?.x ?? 0,
          y: positions[i]?.y ?? i
        };
      }
    });
  }
  

  buildOptions() {

    const rect = this.root.nativeElement.getBoundingClientRect();

    const containerWidth = Math.max(rect.width, 1);
    const containerHeight = Math.max(rect.height, 1);

    const cellW = containerWidth / 3;
    const cellH = containerHeight / 3;

    this.options = {
      gridType: 'fixed',

      fixedColWidth: cellW,
      fixedRowHeight: cellH,
padding:0,
      margin: 0,
      outerMargin: false,

      minCols: 3,
      maxCols: 3,
      minRows: 3,
      maxRows: 3,

      defaultItemCols: 1,
      defaultItemRows: 1,

      minItemCols: 1,
      minItemRows: 1,
      maxItemCols: 3,
      maxItemRows: 3,

      mobileBreakpoint: 0,
      keepFixedHeightInMobile: true,
      setGridSize: true,

      compactType: 'none',
      pushItems: false,

      draggable: { enabled: this.editable },

      displayGrid: 'none',
      resizable: {
        enabled: this.editable,
        handles: {
          s: true,
          e: true,
          n: true,
          w: true,
          se: true,
          sw: true,
          ne: true,
          nw: true
        }
      },
      
      itemResizeStart: () => {
        this.resizeInProgress = true;
        // this.emitLayout();
      },

      itemResizeStop: () => {
        this.resizeInProgress = false;
        this.emitLayout();
      },

      itemChangeCallback: () => this.emitLayout()
    };
  }

  emitLayout() {
    this.layout$.next(this.widgets.map(w => ({...w.item})));
  }

  refreshGrid() {
    if (!this.gridster || !this.gridster.options?.api) return;
    this.gridster.options.api.optionsChanged();
  }

  onItemClick(i: number) {
    this.itemClick.emit(i);
  }
}
